import React from 'react';
import { Button, Text, Image, ImageFocalPointPresets, ImageResizeOptions } from 'wix-ui-tpa/cssVars';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { LoyaltyProgram } from '@wix/ambassador-loyalty-v1-program/types';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';

import settingsParams, { HeaderImageCropPosition } from '../settingsParams';
import { style, classes } from './Header.st.css';
import { IMAGE_ROUTE } from '../../../constants/image-route';
import { DataHook } from '../../../types/data-hook';

type HeaderProps = {
  isLoggedIn: boolean;
  onNavigateToMyRewardsPage(): void;
  hasMyRewardsPage: boolean;
  loyaltyProgram: LoyaltyProgram;
};

const imagePositionMap = {
  [HeaderImageCropPosition.TopLeft]: ImageFocalPointPresets.topLeft,
  [HeaderImageCropPosition.Top]: ImageFocalPointPresets.topCenter,
  [HeaderImageCropPosition.TopRight]: ImageFocalPointPresets.topRight,
  [HeaderImageCropPosition.Left]: ImageFocalPointPresets.centerLeft,
  [HeaderImageCropPosition.Middle]: ImageFocalPointPresets.center,
  [HeaderImageCropPosition.Right]: ImageFocalPointPresets.centerRight,
  [HeaderImageCropPosition.BottomLeft]: ImageFocalPointPresets.bottomLeft,
  [HeaderImageCropPosition.Bottom]: ImageFocalPointPresets.bottomCenter,
  [HeaderImageCropPosition.BottomRight]: ImageFocalPointPresets.bottomRight,
};

export const Header = ({ isLoggedIn, onNavigateToMyRewardsPage, hasMyRewardsPage, loyaltyProgram }: HeaderProps) => {
  const settings = useSettings();
  const styles = useStyles();
  const { isMobile } = useEnvironment();
  const { t } = useTranslation();
  const programName = settings.get(settingsParams.programName);
  const programTitle = settings.get(settingsParams.programTitle);
  const backgroundImage = settings.get(settingsParams.headerImageNew);
  const backgroundImagePosition = settings.get(settingsParams.headerImageCropPosition) as HeaderImageCropPosition;
  const buttonText = settings.get(settingsParams.buttonLoggedIn);
  const buttonTextAnonymous = settings.get(settingsParams.buttonAnonymous);
  const showProgramName = styles
    .getStylesForAllBreakpoints()
    .some((breakpointStyles) => breakpointStyles.booleans.showProgramName);
  const showProgramTitle =
    styles.getStylesForAllBreakpoints().some((breakpointStyles) => breakpointStyles.booleans.showProgramTitle) &&
    !!programTitle.trim();
  const showIcon = isMobile
    ? false
    : styles.getStylesForAllBreakpoints().some((breakpointStyles) => breakpointStyles.booleans.headerSplit);
  const showLoyaltyProgramName =
    settings.get(settingsParams.programNameChanged) === false &&
    programName === settings.getDefaultValue(settingsParams.programName) &&
    loyaltyProgram.name;
  const showCoverImage =
    styles.getStylesForAllBreakpoints().some((breakpointStyles) => breakpointStyles.booleans.showCoverImage) &&
    backgroundImage;

  return (
    <div
      data-hook={DataHook.Header}
      className={style(classes.root, {
        isMobile,
      })}
    >
      {showCoverImage && (
        <Image
          data-hook={DataHook.CoverImage}
          className={classes.backgroundImage}
          src={backgroundImage}
          focalPoint={imagePositionMap[backgroundImagePosition]}
          resize={ImageResizeOptions.cover}
          fluid
        />
      )}
      <div className={classes.content}>
        {showIcon && (
          <img
            alt={t('app.image.points-icon')}
            className={classes.icon}
            src={IMAGE_ROUTE(loyaltyProgram.pointDefinition?.icon?.url ?? '')}
          />
        )}
        {showProgramName && (
          <Text tagName="h1" className={classes.title} data-hook={DataHook.ProgramName}>
            {showLoyaltyProgramName ? loyaltyProgram.name : programName}
          </Text>
        )}
        {showProgramTitle && (
          <Text tagName="p" className={classes.subtitle} data-hook={DataHook.ProgramTitle}>
            {programTitle}
          </Text>
        )}

        {(hasMyRewardsPage || !isLoggedIn) && (
          <Button
            upgrade
            data-hook={DataHook.Button}
            className={classes.button}
            onClick={() => onNavigateToMyRewardsPage()}
          >
            {isLoggedIn ? buttonText : buttonTextAnonymous}
          </Button>
        )}
      </div>
    </div>
  );
};
